import { mapValues, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import {
  getComponentDesignQuantities,
  getFlattenedChildrenWithCalculatedQuantity,
  getInsertionsWithPendingChangesGroupedByObjectGuid,
} from '@paragon/layout-models';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { RootState } from '..';
import { getAnalysisSetsMap } from './analysisSetsSelectors';
import { getActiveComponentDesignGuid } from './getActiveComponentDesignSelector';
import { getActiveStation } from './getActiveStation';
import { getCommittedComponentDesigns } from './getComponentDesignsSelectors';
import { getActiveJobLayoutModel } from './jobContextSelectors';
import { getGroupedProductionComponentInstancesForActiveScheduleLineGroup } from './scheduleStationSelectors';

export const getComponentDesignGuidToQuantity = createSelector(
  (state: RootState) => state.application.openContext,
  getFlattenedChildrenWithCalculatedQuantity,
  getActiveStation,
  getGroupedProductionComponentInstancesForActiveScheduleLineGroup,
  (state: RootState) => state.productionModels.analysisSetGuidToComponentDesignGuid,
  (state: RootState) => state.sharedComponentSet.sharedComponentSet,
  getAnalysisSetsMap,
  getActiveJobLayoutModel,
  getCommittedComponentDesigns,
  (
    openContext,
    getComponentDesignQuantitiesWithModels,
    activeStation,
    groupedProductionComponentInstances,
    productionModelsAnalysisSetGuidToComponentDesignGuid,
    sharedComponentSet,
    analysisSetsMap,
    activeJobLayoutModel,
    committedComponentDesigns,
  ): { [componentDesignGuid: string]: number } => {
    if (!openContext)
      return committedComponentDesigns.reduce(
        (guidToQuantity, componentDesign) => {
          guidToQuantity[componentDesign.guid] = 1;
          return guidToQuantity;
        },
        {} as { [componentDesignGuid: string]: number },
      );

    switch (openContext.type) {
      case 'project': {
        return getComponentDesignQuantities(getComponentDesignQuantitiesWithModels(openContext.projectGuid));
      }
      case 'station':
        if (!activeStation) return {};
        const stationComponentDesigns = mapValues(activeStation.componentDesigns, ({ quantity }) => quantity ?? 1);
        return {
          ...stationComponentDesigns,
          // Include a quantity of 1 for unpersisted component designs opened at the station
          ...committedComponentDesigns
            .filter((componentDesign) => !(componentDesign.guid in stationComponentDesigns))
            .reduce(
              (guidToQuantity, componentDesign) => {
                guidToQuantity[componentDesign.guid] = 1;
                return guidToQuantity;
              },
              {} as { [componentDesignGuid: string]: number },
            ),
        };
      case 'schedule-station':
        if (!groupedProductionComponentInstances) return {};
        return groupedProductionComponentInstances.reduce<{ [componentDesignGuid: string]: number }>(
          (componentDesignGuidToQuantity, groupedInstance) => {
            const componentDesignGuid =
              productionModelsAnalysisSetGuidToComponentDesignGuid[groupedInstance.analysisSetGuid];
            componentDesignGuidToQuantity[componentDesignGuid] =
              (componentDesignGuidToQuantity[componentDesignGuid] ?? 0) + groupedInstance.quantity;
            return componentDesignGuidToQuantity;
          },
          {},
        );
      case 'shared-component-set':
        if (!sharedComponentSet) return {};
        return sharedComponentSet.analysisSetGuidsWithQuantities.reduce<{ [componentDesignGuid: string]: number }>(
          (componentDesignGuidToQuantity, analysisSetGuidWithQuantity) => {
            const { analysisSetGuid, quantity } = analysisSetGuidWithQuantity;
            const analysisSet = analysisSetsMap.get(analysisSetGuid);
            if (!analysisSet) return componentDesignGuidToQuantity;
            const componentDesignGuid = analysisSet.componentDesign.guid;
            componentDesignGuidToQuantity[componentDesignGuid] =
              (componentDesignGuidToQuantity[componentDesignGuid] ?? 0) + quantity;
            return componentDesignGuidToQuantity;
          },
          {},
        );
      case 'job':
        if (!activeJobLayoutModel) return {};
        return Object.fromEntries(activeJobLayoutModel.componentDesignGuids.map((guid) => [guid, 1]));
      default:
        assertExhaustiveSwitch(openContext);
    }
  },
);

export const getActiveComponentDesignQuantity = createSelector(
  getActiveComponentDesignGuid,
  getComponentDesignGuidToQuantity,
  (state: RootState) => state.application.openContext,

  (componentDesignGuid, componentDesignGuidToQuantity, openContext) => {
    if (!componentDesignGuid) return 0;
    if (!openContext) return 1;

    // FIXME Ideally we would verify that we're not falling back to a quantity of 1 at unexpected times, but there
    //  are quite a few edges case to consider, including not crashing before the project has finished loading, not
    //  crashing when viewing an uncommitted component design, and not crashing when replacing a regenerated
    //  component design
    return componentDesignGuidToQuantity[componentDesignGuid] ?? 1;
  },
);

export const canActiveComponentDesignQuantityBeEdited = createSelector(
  getActiveComponentDesignGuid,
  getInsertionsWithPendingChangesGroupedByObjectGuid,
  (componentDesignGuid, insertions) => {
    if (componentDesignGuid == null) return false;

    const insertionsForComponentDesign = insertions[componentDesignGuid];
    if (!insertionsForComponentDesign) return false;

    const hasLayoutInsertion = insertionsForComponentDesign.some((insertion) => insertion.type === 'LayoutComponent');
    return (
      !hasLayoutInsertion && uniq(insertionsForComponentDesign.map((insertion) => insertion.parentGuid)).length === 1
    );
  },
);
